/**
 * Lightweight frame profiler for hunting lag across machines.
 *
 * Timings are wall-clock (performance.now). Enable via debug HUD (backtick).
 * A/B flags skip expensive work so you can isolate lighting vs everything else.
 */

const live = new Map();
let displayBuckets = {};
let displayWindowT0 = 0;

const stats = Object.create(null);

const flags = {
    lighting: true,
};

let frameT0 = 0;
let lastWallT0 = 0;
let lastFrameMs = 0;
let fps = 0;
let fpsFrames = 0;
let fpsWindowT0 = 0;
let entitySampleAt = 0;
let fullEntitySampleAt = 0;
let entityCount = 0;
let enabled = false;

function ensureLive(name) {
    let b = live.get(name);
    if (!b) {
        b = { totalMs: 0, calls: 0, lastMs: 0, maxMs: 0 };
        live.set(name, b);
    }
    return b;
}

function record(name, ms) {
    const b = ensureLive(name);
    b.totalMs += ms;
    b.calls += 1;
    b.lastMs = ms;
    if (ms > b.maxMs) b.maxMs = ms;
}

function rollDisplayBuckets(now) {
    if (!displayWindowT0) displayWindowT0 = now;
    if (now - displayWindowT0 < 500) return;

    const next = {};
    for (const [name, b] of live) {
        next[name] = {
            avgMs: b.calls ? b.totalMs / b.calls : 0,
            lastMs: b.lastMs,
            maxMs: b.maxMs,
            calls: b.calls,
        };
        b.totalMs = 0;
        b.calls = 0;
        b.maxMs = 0;
    }
    displayBuckets = next;
    displayWindowT0 = now;
}

export function setPerfEnabled(on) {
    enabled = !!on;
    if (!enabled) {
        live.clear();
        displayBuckets = {};
        fpsFrames = 0;
        fpsWindowT0 = 0;
        displayWindowT0 = 0;
        lastWallT0 = 0;
    }
}

export function isPerfEnabled() {
    return enabled;
}

export function perfFlag(name) {
    return flags[name] !== false;
}

export function togglePerfFlag(name) {
    if (!(name in flags)) return false;
    flags[name] = !flags[name];
    return flags[name];
}

export function getPerfFlags() {
    return { ...flags };
}

export function setPerfStat(key, value) {
    stats[key] = value;
}

export function perfBegin(name) {
    if (!enabled) return null;
    return { name, t0: performance.now() };
}

export function perfEnd(mark) {
    if (!enabled || !mark) return;
    record(mark.name, performance.now() - mark.t0);
}

export function perfMeasure(name, fn) {
    if (!enabled) return fn();
    const t0 = performance.now();
    try {
        return fn();
    } finally {
        record(name, performance.now() - t0);
    }
}

/** Call at the start of the profiled frame (debug HUD onUpdate). */
export function perfFrameBegin() {
    if (!enabled) return;
    frameT0 = performance.now();
}

/** Call at the end of the same onUpdate; updates FPS + optional entity sample. */
export function perfFrameEnd() {
    if (!enabled) return;

    const now = performance.now();
    // Wall-clock since previous frame end (not KAPLAY's clamped dt).
    if (lastWallT0 > 0) {
        lastFrameMs = now - lastWallT0;
    }
    lastWallT0 = now;
    // Unused begin mark kept for callers; HUD prefers inter-frame wall time.
    void frameT0;

    if (!fpsWindowT0) fpsWindowT0 = now;
    fpsFrames += 1;
    const elapsed = now - fpsWindowT0;
    if (elapsed >= 500) {
        fps = (fpsFrames * 1000) / elapsed;
        fpsFrames = 0;
        fpsWindowT0 = now;
    }

    rollDisplayBuckets(now);

    if (now - entitySampleAt > 1000) {
        entitySampleAt = now;
        try {
            if (now - fullEntitySampleAt > 5000) {
                fullEntitySampleAt = now;
                entityCount = get("*", { recursive: true }).length;
                stats.entitySample = entityCount;
            } else if (stats.mapTiles != null) {
                entityCount = stats.entitySample ?? stats.mapTiles;
            }
        } catch {
            entityCount = 0;
        }
    }
}

export function getPerfSnapshot() {
    let engineDtMs = 0;
    try {
        if (typeof dt === "function") engineDtMs = dt() * 1000;
    } catch {
        engineDtMs = 0;
    }

    return {
        fps,
        frameMs: lastFrameMs,
        engineDtMs,
        entityCount,
        dpr: typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1,
        pixelDensityMin: 2,
        flags: { ...flags },
        stats: { ...stats },
        buckets: displayBuckets,
    };
}

export function dumpPerfToConsole() {
    const snap = getPerfSnapshot();
    console.log("[perf]", {
        fps: Number(snap.fps.toFixed(1)),
        frameMs: Number(snap.frameMs.toFixed(2)),
        entities: snap.entityCount,
        dpr: snap.dpr,
        flags: snap.flags,
        stats: snap.stats,
        buckets: Object.fromEntries(
            Object.entries(snap.buckets).map(([k, v]) => [
                k,
                {
                    avgMs: Number(v.avgMs.toFixed(2)),
                    maxMs: Number(v.maxMs.toFixed(2)),
                    calls: v.calls,
                },
            ]),
        ),
    });
    return snap;
}
